import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountMessageSettings } from '@/hooks/useAccountMessageSettings'
import { useContactManagement } from '@/hooks/useContactManagement'
import { ensureFluxoImageDataUrl, resolveFluxoAudioUrl } from '@/lib/fluxoMedia'
import { runFluxo } from '@/lib/fluxoRunner'
import { applySenderPrefix } from '@/lib/messageSenderPrefix'
import { renderMessageVariables } from '@/lib/messageVariables'
import type { Fluxo } from '@/hooks/useFluxos'
import type { Database } from '@/lib/supabase'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface UseFluxoSenderParams {
  selectedContact: AccountContact | null
  sendMessage: (contactJid: string, content: string) => Promise<boolean>
  sendImage: (contactJid: string, imageBase64: string, caption?: string, fileName?: string) => Promise<boolean>
  sendAudio: (contactJid: string, audioUrl: string) => Promise<boolean>
  onContactUpdate?: () => void
  /** Sobe a conversa na lista após um bloco de mensagem enviado com sucesso. */
  onConversationActivity?: (contactId: string) => void
}

interface FluxoSenderProgress {
  current: number
  total: number
}

interface UseFluxoSenderReturn {
  sendFluxo: (fluxo: Fluxo) => Promise<void>
  sendingFluxo: boolean
  progress: FluxoSenderProgress | null
}

export const useFluxoSender = ({
  selectedContact,
  sendMessage,
  sendImage,
  sendAudio,
  onContactUpdate,
  onConversationActivity,
}: UseFluxoSenderParams): UseFluxoSenderReturn => {
  const { user } = useAuth()
  const { showUserNameInMessages } = useAccountMessageSettings()
  const { assignAssistant, enableAssistant } = useContactManagement()
  const [sendingFluxo, setSendingFluxo] = useState(false)
  const [progress, setProgress] = useState<FluxoSenderProgress | null>(null)
  const runningRef = useRef(false)

  const sendFluxo = useCallback(async (fluxo: Fluxo): Promise<void> => {
    if (!selectedContact) {
      toast.error('Selecione um contato para enviar o fluxo')
      return
    }

    if (runningRef.current) {
      toast.warning('Aguarde o fluxo atual terminar de ser enviado')
      return
    }

    // O contato é capturado no início: se o usuário trocar de conversa no meio
    // do envio, o restante do fluxo continua indo para quem foi escolhido.
    const contact = selectedContact

    const bumpConversation = () => {
      onConversationActivity?.(contact.id)
    }

    runningRef.current = true
    setSendingFluxo(true)
    setProgress(null)

    const toastId = toast.loading(`Enviando fluxo "${fluxo.name}"...`)

    try {
      const result = await runFluxo(fluxo, {
        sendText: async (content) => {
          const rendered = renderMessageVariables(content, contact)
          const text = applySenderPrefix(rendered, user?.name, showUserNameInMessages)
          const ok = await sendMessage(contact.jid, text)
          if (ok) bumpConversation()
          return ok
        },
        sendImage: async (source, fileName) => {
          const dataUrl = await ensureFluxoImageDataUrl(source)
          const ok = await sendImage(contact.jid, dataUrl, '', fileName)
          if (ok) bumpConversation()
          return ok
        },
        sendAudio: async (source, fileName) => {
          const audioUrl = await resolveFluxoAudioUrl(source, fileName)
          const ok = await sendAudio(contact.jid, audioUrl)
          if (ok) bumpConversation()
          return ok
        },
        activateAssistant: async (assistantId) => {
          const assigned = await assignAssistant(contact.id, assistantId)
          if (!assigned) return false

          // assignAssistant só ativa quando o contato ainda não tinha assistente;
          // no fluxo a intenção é sempre deixar o assistente escolhido respondendo.
          const enabled = await enableAssistant(contact.id)
          if (enabled) onContactUpdate?.()
          return enabled
        },
        onProgress: ({ current, total }) => {
          setProgress({ current, total })
          toast.loading(`Enviando fluxo "${fluxo.name}" (${current}/${total})`, { id: toastId })
        },
      })

      if (result.status === 'empty') {
        toast.error(`O fluxo "${fluxo.name}" não tem blocos preenchidos`, { id: toastId })
        return
      }

      if (result.status === 'failed') {
        toast.error(
          `Fluxo interrompido em ${result.sent + 1} de ${result.total}: ${result.error}`,
          { id: toastId }
        )
        return
      }

      toast.success(
        `Fluxo "${fluxo.name}" enviado (${result.sent} ${result.sent === 1 ? 'bloco' : 'blocos'})`,
        { id: toastId }
      )
    } catch (err) {
      console.error('💥 [useFluxoSender] Erro ao enviar fluxo:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar fluxo', { id: toastId })
    } finally {
      runningRef.current = false
      setSendingFluxo(false)
      setProgress(null)
    }
  }, [
    selectedContact,
    sendMessage,
    sendImage,
    sendAudio,
    assignAssistant,
    enableAssistant,
    onContactUpdate,
    onConversationActivity,
    user?.name,
    showUserNameInMessages,
  ])

  return { sendFluxo, sendingFluxo, progress }
}
